import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, Treemap } from "recharts";
import { Grid3x3, LineChart, X } from "lucide-react";
import { api } from "@/lib/api";
import { WatchAdd } from "@/components/WatchAdd";

const color = (p) => {
  const a = Math.min(Math.abs(p) / 3, 1);
  if (p >= 0) return `rgb(${Math.round(16 + (1 - a) * 20)}, ${Math.round(70 + a * 115)}, ${Math.round(60 + a * 40)})`;
  return `rgb(${Math.round(90 + a * 150)}, ${Math.round(30 + (1 - a) * 20)}, ${Math.round(45 + (1 - a) * 15)})`;
};

const Tile = (props) => {
  const { x, y, width, height, depth, name, change_pct, onPick } = props;
  if (depth !== 1) return null;
  const big = width > 46 && height > 28;
  return (
    <g style={{ cursor: "pointer" }} onClick={() => onPick(props)} data-testid={`heatmap-tile-${name}`}>
      <title>{`${name} ${change_pct > 0 ? "+" : ""}${change_pct}%`}</title>
      <rect x={x} y={y} width={width} height={height} fill={color(change_pct)} stroke="#ffffff" strokeWidth={1} />
      {big && <text x={x + width / 2} y={y + height / 2 - 2} textAnchor="middle" fill="#f8fafc" fontSize={Math.min(13, width / 6)} fontFamily="JetBrains Mono" fontWeight={700}>{name}</text>}
      {big && <text x={x + width / 2} y={y + height / 2 + 11} textAnchor="middle" fill="#e2e8f0" fontSize={10} fontFamily="JetBrains Mono">{`${change_pct > 0 ? "+" : ""}${change_pct}%`}</text>}
    </g>
  );
};

export default function Heatmap() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const load = () => api.get("/today/heatmap").then((r) => setD(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  const total = useMemo(() => (d?.sectors || []).reduce((t, g) => t + g.turnover_cr, 0) || 1, [d]);
  const [picked, setPicked] = useState(null);
  const pick = (t) => t.instrument_id && setPicked({ id: t.instrument_id, symbol: t.name, chg: t.change_pct });
  return (
    <div className="space-y-3" data-testid="heatmap-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Grid3x3 className="h-5 w-5 text-blue-600" /> F&O Heatmap</h1>
          <p className="text-xs text-slate-500 mt-0.5 font-mono">all {d?.count ?? "—"} F&O stocks · grouped by sector · tile size = turnover · colour = % move · click a tile for its chart</p>
        </div>
        <div className="flex items-center gap-1 text-[10px] font-mono text-slate-500" data-testid="heatmap-legend">
          {[-3, -2, -1, 0, 1, 2, 3].map((p) => <span key={p} className="px-1.5 py-0.5 text-white font-semibold" style={{ background: color(p), textShadow: "0 0 2px rgba(0,0,0,0.45)" }}>{p > 0 ? "+" : ""}{p}%</span>)}
        </div>
      </div>
      {!d ? <p className="p-6 text-xs font-mono text-slate-500">Loading…</p> : (
        <div className="flex flex-wrap gap-1" data-testid="heatmap-canvas">
          {d.sectors.map((g) => {
            const share = g.turnover_cr / total;
            return (
              <div key={g.sector} className="border border-slate-200 bg-white flex flex-col" data-testid={`heatmap-sector-${g.sector.replace(/\W+/g, "-")}`}
                style={{ flex: `${Math.max(share * 100, 3)} 1 ${Math.max(150, share * 1600)}px`, height: share > 0.08 ? 340 : 220 }}>
                <div className="flex items-center justify-between px-1.5 h-5 shrink-0 text-[10px] font-mono">
                  <span className="text-slate-900 font-bold truncate">{g.sector} <span className="text-slate-500 font-normal">{g.stocks.length}</span></span>
                  <span className={g.avg_change >= 0 ? "text-emerald-600" : "text-rose-600"}>{g.avg_change > 0 ? "+" : ""}{g.avg_change}%</span>
                </div>
                <div className="flex-1 min-h-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <Treemap data={g.stocks.map((x) => ({ name: x.symbol, size: Math.max(x.turnover_cr, 0.5), change_pct: x.change_pct, instrument_id: x.instrument_id }))}
                      dataKey="size" isAnimationActive={false} content={<Tile onPick={pick} />} />
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {picked && (
        <div className="fixed bottom-6 right-6 z-50 border border-blue-500 bg-white p-3 w-64 font-mono" data-testid="heatmap-menu">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-slate-900">{picked.symbol} <span className={picked.chg >= 0 ? "text-emerald-600" : "text-rose-600"}>{picked.chg > 0 ? "+" : ""}{picked.chg}%</span></span>
            <button onClick={() => setPicked(null)} data-testid="heatmap-menu-close" className="text-slate-500 hover:text-slate-900"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(`/app/instrument/${picked.id}`)} data-testid="heatmap-menu-chart"
              className="flex-1 h-7 text-xs border border-slate-300 hover:border-blue-500 text-slate-800 flex items-center justify-center gap-1"><LineChart className="h-3.5 w-3.5 text-blue-600" /> Open chart</button>
            <span className="text-[10px] text-slate-500">watchlist</span>
            <WatchAdd instrumentId={picked.id} symbol={picked.symbol} className="h-7 w-7" />
          </div>
        </div>
      )}
    </div>
  );
}
